import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseStore } from './supabaseStore';
import { useRoleStore } from './useRole';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import { getLanguageFromPath } from '@/utils/utils';
import logger from '@/logger';

interface UseSupabaseAuthReturn {
	isLoading: boolean;
	error: string | null;
	role: string | null;
	userId: string | null;
	isKogniUser: boolean;
}

export const useSupabaseAuth = (allowedRolesString?: string): UseSupabaseAuthReturn => {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [userId, setUserId] = useState<string | null>(null);
	const navigate = useNavigate();
	const language = getLanguageFromPath(window.location.pathname) || DEFAULT_LANGUAGE;
	const supabase = useSupabaseStore((state) => state.supabase);
	const { role, saveRole } = useRoleStore();

	const allowedRoles = useMemo(() => {
		if (!allowedRolesString) return undefined;
		return allowedRolesString.split(',').map((r) => r.trim()) as Roles[];
	}, [allowedRolesString]);

	const isRoleAllowed = useCallback(
		(userRole: string): boolean => {
			if (!allowedRoles) return true;
			return allowedRoles.includes(userRole as Roles);
		},
		[allowedRoles]
	);

	const isKogniUser = useCallback(() => {
		return role === Roles.KOGNI_ADMIN;
	}, [role]);

	useEffect(() => {
		const checkSessionAndRole = async () => {
			try {
				setIsLoading(true);

				const {
					data: { session },
					error: sessionError
				} = await supabase.auth.getSession();

				if (sessionError) throw sessionError;

				if (!session) {
					const currentPath = window.location.pathname;
					const returnTo = encodeURIComponent(currentPath);
					navigate(`/${language}/center/support?returnTo=${returnTo}`);
					return;
				}

				setUserId(session.user.id);

				if (role && !isRoleAllowed(role)) {
					logger.error(`User role ${role} not in allowed roles`, allowedRoles);
					navigate(`/${language}/center/customer-support`);
					return;
				}

				const { data: roleData, error: roleError } = await supabase
					.from('user_roles')
					.select('role')
					.eq('user_id', session.user.id)
					.single();

				if (roleError) throw roleError;

				if (!roleData || !roleData.role) {
					throw new Error('No role found for user');
				}

				saveRole(roleData.role);

				if (!isRoleAllowed(roleData.role)) {
					logger.error(`User role ${roleData.role} not in allowed roles`, allowedRoles);
					navigate(`/${language}/center/customer-support`);
					return;
				}

				setError(null);
			} catch (err) {
				logger.error('Error checking session and role:', err);
				setError(err instanceof Error ? err.message : 'Authentication error');
				const currentPath = window.location.pathname;
				const returnTo = encodeURIComponent(currentPath);
				navigate(`/${language}/center/support?returnTo=${returnTo}`);
			} finally {
				setIsLoading(false);
			}
		};

		checkSessionAndRole();
	}, [supabase, language, allowedRolesString, role, saveRole, navigate]);

	return {
		isLoading,
		error,
		role,
		userId,
		isKogniUser: isKogniUser()
	};
};
